// Display-label helpers for partner-provisioned organizations and clients.
//
// Privacy context: partners are not permitted to share a firm's name or a
// client's name with NoticeAI. For partner-onboarded rows the backend stores
// the partner's opaque reference (partner_org_ref / partner_client_ref) in the
// company_name / client_name column instead. So a partner row's "name" is
// usually already the reference — these helpers just make the fallback chain
// explicit and whitespace-safe so titles, breadcrumbs, modals, and avatars
// never render blank.
//
// Render-time only — never round-trip a formatted label back to the server.

// First non-blank string from the arguments, trimmed. Treats whitespace-only
// values as empty (so " " never sneaks through a plain `||`). Returns "" when
// nothing usable is given.
const firstNonBlank = (...vals) => {
  for (const v of vals) {
    const s = (v ?? "").toString().trim();
    if (s) return s;
  }
  return "";
};

// Human label for an organization row. Prefers the stored name, then the
// partner reference, then the internal NoticeAI reference. Falls back to a
// dash so a nameless partner org never renders blank.
// `org` is the API object: { company_name, partner_org_ref, org_reference }.
export const orgDisplayLabel = (org, fallback = "-") =>
  firstNonBlank(org?.company_name, org?.partner_org_ref, org?.org_reference) ||
  fallback;

// Human label for a client row. Prefers the stored name, then the partner
// reference. For partner clients these are usually the same value.
// `client` is the API object: { client_name, partner_client_ref }.
export const clientDisplayLabel = (client, fallback = "-") =>
  firstNonBlank(client?.client_name, client?.partner_client_ref) || fallback;

// Plan / billing-model label for an organization row.
//
// Billing-model change (2026-06): partner-registered organizations are no
// longer assigned a subscription plan — they are billed by usage + annual
// charges. So a partner org has no `subscription_plan`. Rather than showing a
// bare "—" (which reads as "missing data"), show that it is partner-billed.
//
// `org` is the API object: { subscription_plan, partner_id }.
//   - has a plan        → the plan name
//   - partner org, no plan → "Usage-billed (partner)"
//   - neither           → fallback ("—")
export const planDisplayLabel = (org, fallback = "-") => {
  const plan = (org?.subscription_plan ?? "").toString().trim();
  if (plan) return plan;
  if (org?.partner_id) return "Usage-billed (partner)";
  return fallback;
};

// Single uppercase initial for an avatar, derived from any display string.
// Whitespace-safe (trims before taking the first character) and falls back to
// "?" when the source is empty/blank.
export const avatarInitial = (value, fallback = "?") => {
  const s = (value ?? "").toString().trim();
  return (s ? s.charAt(0) : fallback).toUpperCase();
};
